import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Modal,
  Form,
  Alert,
  Badge,
  Table,
  Spinner
} from "react-bootstrap";
import { FaShoppingCart, FaUpload, FaCheck, FaHourglassHalf } from "react-icons/fa";
import Swal from "sweetalert2";
import { packagesService } from "../../services/api";

const Packages = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [note, setNote] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [packages, setPackages] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  useEffect(() => {
    fetchPackages();
    fetchPurchaseHistory();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await packagesService.getPackages();
      if (response.success && response.data && response.data.packages) {
        setPackages(response.data.packages);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setAlertVariant("danger");
      setAlertMessage("ไม่สามารถโหลดข้อมูลแพ็กเกจได้ กรุณาลองใหม่อีกครั้ง");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await packagesService.getPurchaseHistory();
      if (response.success && response.data && response.data.purchases) {
        setPurchaseHistory(response.data.purchases);
      }
    } catch (error) {
      console.error("Error fetching purchase history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setShowModal(true);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setReceipt(e.target.files[0]);
    }
  };

  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  const handleViewDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDetailsModal(true);
  };

  const handlePurchaseSubmit = async () => {
    if (!receipt) {
      setAlertVariant("danger");
      setAlertMessage("กรุณาอัปโหลดสลิปการโอนเงิน");
      setShowAlert(true);
      return;
    }

    try {
      setSubmitting(true);
      
      const purchaseData = {
        package_id: selectedPackage.id,
        receipt_image: receipt,
        note: note || undefined
      };

      const response = await packagesService.submitPurchase(purchaseData);
      
      if (response.success) {
        setShowModal(false);
        setReceipt(null);
        setNote("");
        setAlertVariant("success");
        setAlertMessage(response.message || "ส่งคำสั่งซื้อเรียบร้อย กรุณารอการตรวจสอบจากเจ้าหน้าที่");
        setShowAlert(true);
        
        await fetchPurchaseHistory();
        
        setTimeout(() => {
          setShowAlert(false);
        }, 5000);
      }
    } catch (error) {
      console.error("Error submitting purchase:", error);
      
      let errorMessage = "เกิดข้อผิดพลาดในการส่งคำสั่งซื้อ กรุณาลองใหม่อีกครั้ง";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error.message || errorMessage;
      }
      
      setShowModal(false);
      
      setTimeout(() => {
        Swal.fire({
          icon: "error",
          title: "ข้อผิดพลาด",
          text: errorMessage,
          confirmButtonText: "ตกลง",
          confirmButtonColor: "#f77c1c"
        });
      }, 300);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return price.toLocaleString('th-TH');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending_verification":
        return "warning";
      case "expired":
        return "secondary";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getRemainingText = (purchase) => {
    if (purchase.quota_type === 'monthly') {
      return 'ไม่จำกัดครั้ง';
    }
    if (purchase.quota_type === 'numbers') {
      return `${purchase.number_quota_remaining}/${purchase.number_quota_amount} ครั้ง`;
    }
    return '-';
  };

  return (
    <>
      <div id="content-page" className="content-inner">
        <Container>
          <Row className="row-eq-height">
            <Col md="12">
              <Row className="mb-5">
                <Col md="12">
                  <div className="card-header d-flex justify-content-between">
                    <div className="header-title">
                      <h4 className="card-title">แพ็กเกจ Boost Ads</h4>
                    </div>
                  </div>
                </Col>
              </Row>
              
              {showAlert && (
                <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">กำลังโหลดแพ็กเกจ...</p>
                </div>
              ) : (
                <Row>
                  {packages.map((pkg) => (
                    <Col lg="3" md="6" key={pkg.id} className="mb-4">
                      <Card className="h-100">
                        <Card.Body>
                          <h4 className="text-center mb-3">{pkg.display_name}</h4>
                          <h2 className="text-center text-primary mb-4">{formatPrice(pkg.price)} บาท</h2>
                          <p className="text-center">{pkg.detail}</p>
                          <ul className="list-unstyled">
                            {pkg.ad_features && pkg.ad_features.map((feature, index) => (
                              <li key={index} className="mb-2">
                                <FaCheck className="text-success me-2" /> {feature}
                              </li>
                            ))}
                          </ul>
                        </Card.Body>
                        <Card.Footer className="text-center">
                          <Button 
                            variant="primary" 
                            className="w-100"
                            onClick={() => handlePackageSelect(pkg)}
                            disabled={!pkg.is_active}
                          >
                            <FaShoppingCart className="me-2" /> ซื้อเลย
                          </Button>
                        </Card.Footer>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
              
              <Row className="mt-5">
                <Col md="12">
                  <Card>
                    <Card.Header>
                      <h4 className="card-title">แพ็กเกจของฉัน</h4>
                    </Card.Header>
                    <Card.Body>
                      {historyLoading ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" variant="primary" size="sm" />
                          <p className="mt-2 mb-0">กำลังโหลดประวัติการซื้อ...</p>
                        </div>
                      ) : purchaseHistory.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted">ยังไม่มีประวัติการซื้อแพ็กเกจ</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table striped hover>
                            <thead>
                              <tr>
                                <th>แพ็กเกจ</th>
                                <th>ราคา (บาท)</th>
                                <th>วันที่ซื้อ</th>
                                <th>คงเหลือ</th>
                                <th>สถานะ</th>
                                <th>การกระทำ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseHistory.map((purchase) => (
                                <tr key={purchase.id}>
                                  <td>{purchase.package_display_name}</td>
                                  <td>{formatPrice(purchase.price)}</td>
                                  <td>{formatDate(purchase.purchase_date)}</td>
                                  <td>{getRemainingText(purchase)}</td>
                                  <td>
                                    <Badge bg={getStatusBadgeVariant(purchase.status)}>
                                      {purchase.status_text}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Button 
                                      variant="outline-primary" 
                                      size="sm"
                                      onClick={() => handleViewDetails(purchase)}
                                    >
                                      ดูรายละเอียด
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>รายละเอียดการซื้อแพ็กเกจ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPurchase && (
            <>
              <div className="mb-4">
                <h5>ข้อมูลการซื้อ</h5>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>แพ็กเกจ:</strong> {selectedPurchase.package_display_name}</p>
                    <p><strong>ราคา:</strong> {formatPrice(selectedPurchase.price)} บาท</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>วันที่ซื้อ:</strong> {formatDate(selectedPurchase.purchase_date)}</p>
                  </div>
                </div>
                <p><strong>คงเหลือ:</strong> {getRemainingText(selectedPurchase)}</p>
                <p><strong>สถานะ:</strong> <Badge bg={getStatusBadgeVariant(selectedPurchase.status)}>{selectedPurchase.status_text}</Badge></p>
              </div>
              
              {selectedPurchase.note && (
                <div className="mb-4">
                  <h5>หมายเหตุ</h5>
                  <p>{selectedPurchase.note}</p>
                </div>
              )}
              
              {selectedPurchase.receipt_url && (
                <div className="mb-4">
                  <h5>ใบเสร็จ</h5>
                  <div className="text-center">
                    <img 
                      src={selectedPurchase.receipt_url} 
                      alt="Receipt" 
                      style={{maxWidth: '100%', maxHeight: '400px', border: '1px solid #ddd', borderRadius: '4px'}}
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <a 
                      href={selectedPurchase.receipt_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      ดูรูปแบบเต็ม
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Purchase Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันการซื้อแพ็กเกจ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPackage && (
            <>
              <div className="text-center mb-4">
                <h4>{selectedPackage.display_name}</h4>
                <h3 className="text-primary">{formatPrice(selectedPackage.price)} บาท</h3>
              </div>
              
              <div className="mb-4">
                <h5>วิธีการชำระเงิน</h5>
                <p>โอนเงินมาที่บัญชี:</p>
                <ul className="list-unstyled">
                  <li>ธนาคารกสิกรไทย</li>
                  <li>เลขที่บัญชี: 123-4-56789-0</li>
                  <li>ชื่อบัญชี: บริษัท รวยชัวร์ จำกัด</li>
                </ul>
              </div>
              
              <Form.Group controlId="formFile" className="mb-3">
                <Form.Label>อัปโหลดสลิปการโอนเงิน *</Form.Label>
                <Form.Control 
                  type="file" 
                  onChange={handleFileChange}
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  required
                />
                <Form.Text className="text-muted">
                  รองรับไฟล์ JPG, PNG, PDF (ไม่เกิน 5MB)
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="formNote">
                <Form.Label>หมายเหตุ (ถ้ามี)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={note}
                  onChange={handleNoteChange}
                  placeholder="ระบุหมายเหตุเพิ่มเติม"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
            ยกเลิก
          </Button>
          <Button variant="primary" onClick={handlePurchaseSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                กำลังส่ง...
              </>
            ) : (
              <>
                <FaHourglassHalf className="me-2" /> ยืนยันการสั่งซื้อ
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Packages;
